import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { FileText, Download, CheckCircle, XCircle, Clock, Eye, AlertCircle } from "lucide-react";

const ApplicationsTab = () => {
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [applicationType, setApplicationType] = useState("club"); // "club" or "company"
  const [selectedApp, setSelectedApp] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchApplications();
  }, [applicationType]);

  useEffect(() => {
    filterApplications();
  }, [statusFilter, applications]);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("No authentication token found");
        setLoading(false);
        return;
      }
      
      const endpoint = applicationType === "club" 
        ? `${API}/admin/club-applications`
        : `${API}/admin/company-applications`;
      
      console.log("Fetching applications from:", endpoint);
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log("Applications response:", response.data);
      
      const apps = response.data.applications || response.data || [];
      setApplications(apps);
    } catch (error) {
      console.error("Error fetching applications:", error);
      console.error("Error response:", error.response?.data);
      toast.error(error.response?.data?.detail || "Failed to load applications");
    } finally {
      setLoading(false);
    }
  };

  const filterApplications = () => {
    if (statusFilter === "all") {
      setFilteredApplications(applications);
    } else {
      setFilteredApplications(applications.filter(app => app.status === statusFilter));
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: "bg-yellow-100", text: "text-yellow-800", icon: <Clock className="w-4 h-4" /> },
      "under_review": { bg: "bg-blue-100", text: "text-blue-800", icon: <Eye className="w-4 h-4" /> },
      approved: { bg: "bg-green-100", text: "text-green-800", icon: <CheckCircle className="w-4 h-4" /> },
      rejected: { bg: "bg-red-100", text: "text-red-800", icon: <XCircle className="w-4 h-4" /> }
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.icon}
        {status.replace("_", " ").toUpperCase()}
      </span>
    );
  };

  const downloadDocument = async (applicationId, docType, filename) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/admin/club-applications/${applicationId}/document/${docType}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Document downloaded");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download document");
    }
  };

  const updateStatus = async (applicationId, newStatus) => {
    try {
      setProcessing(true);
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/admin/club-applications/${applicationId}`,
        { status: newStatus, admin_notes: adminNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Application ${newStatus}`);
      setShowDetailDialog(false);
      fetchApplications();
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Failed to update application");
    } finally {
      setProcessing(false);
    }
  };

  const approveApplication = async (applicationId) => {
    const isClub = applicationType === "club";
    const confirmMessage = isClub 
      ? "Are you sure you want to approve this application? This will create a club account."
      : "Are you sure you want to approve this company application?";
    
    if (!confirm(confirmMessage)) {
      return;
    }

    try {
      setProcessing(true);
      const token = localStorage.getItem("token");
      const endpoint = isClub
        ? `${API}/admin/club-applications/${applicationId}/approve`
        : `${API}/admin/company-applications/${applicationId}/approve`;
      
      await axios.post(
        endpoint,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const successMessage = isClub
        ? "Application approved! Club account created and welcome email sent."
        : "Application approved! Customer account created and login credentials emailed.";
      
      toast.success(successMessage);
      setShowDetailDialog(false);
      fetchApplications();
    } catch (error) {
      console.error("Approval error:", error);
      toast.error(error.response?.data?.detail || "Failed to approve application");
    } finally {
      setProcessing(false);
    }
  };

  const viewDetails = (app) => {
    setSelectedApp(app);
    setAdminNotes(app.admin_notes || "");
    setShowDetailDialog(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Total Applications</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{applications.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Pending</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              {applications.filter(a => a.status === 'pending').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Approved</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {applications.filter(a => a.status === 'approved').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Rejected</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {applications.filter(a => a.status === 'rejected').length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Applications List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <CardTitle>{applicationType === "club" ? "Club" : "Company"} Applications</CardTitle>
              <div className="flex gap-2">
                <Button 
                  variant={applicationType === "club" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setApplicationType("club")}
                >
                  Clubs
                </Button>
                <Button 
                  variant={applicationType === "company" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setApplicationType("company")}
                >
                  Companies
                </Button>
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Applications</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="under_review">Under Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-semibold text-sm text-gray-600">
                    {applicationType === "club" ? "Club" : "Company"} Name
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-sm text-gray-600">Location</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm text-gray-600">Contact</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm text-gray-600">Submitted</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm text-gray-600">Status</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredApplications.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-8 text-gray-500">
                      No applications found
                    </td>
                  </tr>
                ) : (
                  filteredApplications.map((app) => (
                    <tr key={app.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="font-medium">
                          {applicationType === "club" ? app.club_info?.name : app.company_info?.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {applicationType === "club" ? app.club_info?.type : app.profile?.organisation_type}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-sm">{app.address?.region || 'N/A'}</div>
                        <div className="text-xs text-gray-500">{app.address?.country || 'N/A'}</div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-sm">
                          {applicationType === "club" 
                            ? `${app.contact?.first_name} ${app.contact?.last_name}`
                            : app.contact?.name
                          }
                        </div>
                        <div className="text-xs text-gray-500">
                          {applicationType === "club" ? app.contact?.email : app.contact?.email}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-sm">
                          {new Date(app.submitted_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {getStatusBadge(app.status)}
                      </td>
                      <td className="py-3 px-4">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => viewDetails(app)}
                        >
                          Review
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      {selectedApp && (
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                Application Details - {applicationType === "club" ? selectedApp.club_info?.name : selectedApp.company_info?.name}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-6">
              {/* Status */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <div className="text-sm text-gray-600">Status</div>
                  <div className="mt-1">{getStatusBadge(selectedApp.status)}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-600">Submitted</div>
                  <div className="text-sm font-medium">
                    {new Date(selectedApp.submitted_at).toLocaleString()}
                  </div>
                </div>
              </div>

              {/* Club/Company Info */}
              <div>
                <h4 className="font-semibold mb-3">{applicationType === "club" ? "Club Information" : "Company Information"}</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {applicationType === "club" ? (
                    <>
                      <div>
                        <span className="text-gray-600">Name:</span>
                        <span className="ml-2 font-medium">{selectedApp.club_info?.name}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Type:</span>
                        <span className="ml-2 font-medium">{selectedApp.club_info?.type}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Established:</span>
                        <span className="ml-2 font-medium">{selectedApp.club_info?.year_established}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Affiliated Body:</span>
                        <span className="ml-2 font-medium">{selectedApp.club_info?.affiliated_body}</span>
                      </div>
                      {selectedApp.club_info?.registration_number && (
                        <div className="col-span-2">
                          <span className="text-gray-600">Registration #:</span>
                          <span className="ml-2 font-medium">{selectedApp.club_info.registration_number}</span>
                        </div>
                      )}
                      {selectedApp.club_info?.website && (
                        <div className="col-span-2">
                          <span className="text-gray-600">Website:</span>
                          <a href={selectedApp.club_info.website} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-600 hover:underline">
                            {selectedApp.club_info.website}
                          </a>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div>
                        <span className="text-gray-600">Name:</span>
                        <span className="ml-2 font-medium">{selectedApp.company_info?.name}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Type:</span>
                        <span className="ml-2 font-medium">{selectedApp.profile?.organisation_type}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Established:</span>
                        <span className="ml-2 font-medium">{selectedApp.profile?.year_established}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Employees:</span>
                        <span className="ml-2 font-medium">{selectedApp.profile?.number_of_employees}</span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-gray-600">Annual Budget:</span>
                        <span className="ml-2 font-medium">{selectedApp.profile?.annual_budget}</span>
                      </div>
                      {selectedApp.company_info?.website && (
                        <div className="col-span-2">
                          <span className="text-gray-600">Website:</span>
                          <a href={selectedApp.company_info.website} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-600 hover:underline">
                            {selectedApp.company_info.website}
                          </a>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Social Media */}
                {((applicationType === "club" && selectedApp.club_info?.social_media) || 
                  (applicationType === "company" && selectedApp.company_info?.social_media)) && 
                  Object.values(applicationType === "club" ? selectedApp.club_info.social_media : selectedApp.company_info.social_media).some(v => v) && (
                  <div className="mt-4">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Social Media</h5>
                    <div className="flex flex-wrap gap-2">
                      {(applicationType === "club" ? selectedApp.club_info.social_media?.facebook : selectedApp.company_info.social_media?.facebook) && (
                        <a href={applicationType === "club" ? selectedApp.club_info.social_media.facebook : selectedApp.company_info.social_media.facebook} target="_blank" rel="noopener noreferrer" className="text-xs px-3 py-1 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200">
                          Facebook
                        </a>
                      )}
                      {(applicationType === "club" ? selectedApp.club_info.social_media?.twitter : selectedApp.company_info.social_media?.twitter) && (
                        <a href={applicationType === "club" ? selectedApp.club_info.social_media.twitter : selectedApp.company_info.social_media.twitter} target="_blank" rel="noopener noreferrer" className="text-xs px-3 py-1 bg-sky-100 text-sky-700 rounded-full hover:bg-sky-200">
                          Twitter/X
                        </a>
                      )}
                      {(applicationType === "club" ? selectedApp.club_info.social_media?.instagram : selectedApp.company_info.social_media?.instagram) && (
                        <a href={applicationType === "club" ? selectedApp.club_info.social_media.instagram : selectedApp.company_info.social_media.instagram} target="_blank" rel="noopener noreferrer" className="text-xs px-3 py-1 bg-pink-100 text-pink-700 rounded-full hover:bg-pink-200">
                          Instagram
                        </a>
                      )}
                      {(applicationType === "club" ? selectedApp.club_info.social_media?.linkedin : selectedApp.company_info.social_media?.linkedin) && (
                        <a href={applicationType === "club" ? selectedApp.club_info.social_media.linkedin : selectedApp.company_info.social_media.linkedin} target="_blank" rel="noopener noreferrer" className="text-xs px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full hover:bg-indigo-200">
                          LinkedIn
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Address */}
              <div>
                <h4 className="font-semibold mb-3">Address</h4>
                <div className="text-sm">
                  <div>{selectedApp.address?.address}</div>
                  <div>{selectedApp.address?.city}, {selectedApp.address?.region}</div>
                  <div>{selectedApp.address?.postal_code}</div>
                  <div>{selectedApp.address?.country}</div>
                </div>
              </div>

              {/* Contact */}
              <div>
                <h4 className="font-semibold mb-3">Contact Person</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Name:</span>
                    <span className="ml-2 font-medium">
                      {applicationType === "club" 
                        ? `${selectedApp.contact?.first_name} ${selectedApp.contact?.last_name}`
                        : selectedApp.contact?.name
                      }
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Position:</span>
                    <span className="ml-2 font-medium">
                      {applicationType === "club" ? selectedApp.contact?.position : selectedApp.contact?.role}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Email:</span>
                    <span className="ml-2 font-medium">{selectedApp.contact?.email}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Phone:</span>
                    <span className="ml-2 font-medium">{selectedApp.contact?.phone}</span>
                  </div>
                </div>
              </div>

              {/* Banking - Only for club applications */}
              {applicationType === "club" && selectedApp.banking && (
                <div>
                  <h4 className="font-semibold mb-3">Banking Information</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Bank Country:</span>
                      <span className="ml-2 font-medium">{selectedApp.banking?.country}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Account Holder:</span>
                      <span className="ml-2 font-medium">{selectedApp.banking?.account_holder_name}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Documents - Only for club applications */}
              {applicationType === "club" && (
                <div>
                  <h4 className="font-semibold mb-3">Documents</h4>
                <div className="space-y-2">
                  {selectedApp.documents?.affiliation && (
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-blue-600" />
                        <div>
                          <div className="font-medium text-sm">Affiliation Document</div>
                          <div className="text-xs text-gray-500">
                            {selectedApp.documents.affiliation.filename} ({(selectedApp.documents.affiliation.size / 1024).toFixed(1)}KB)
                          </div>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadDocument(
                          selectedApp.id,
                          'affiliation',
                          selectedApp.documents.affiliation.filename
                        )}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </Button>
                    </div>
                  )}
                  {selectedApp.documents?.bank && (
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-green-600" />
                        <div>
                          <div className="font-medium text-sm">Bank Verification Document</div>
                          <div className="text-xs text-gray-500">
                            {selectedApp.documents.bank.filename} ({(selectedApp.documents.bank.size / 1024).toFixed(1)}KB)
                          </div>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadDocument(
                          selectedApp.id,
                          'bank',
                          selectedApp.documents.bank.filename
                        )}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              )}

              {/* India CSR Compliance Section - Only for Indian Clubs */}
              {applicationType === "club" && selectedApp.address?.country === "India" && selectedApp.india_csr_compliance && (
                <div className="space-y-4 p-4 bg-gradient-to-br from-orange-50 to-green-50 rounded-lg border-2 border-orange-300">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center">
                      <FileText className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="font-bold text-orange-900">India CSR Compliance Review</h3>
                  </div>

                  {/* Statutory Registration Details */}
                  <div className="space-y-3 p-3 bg-white rounded border">
                    <h4 className="font-semibold text-sm text-gray-800">Statutory Registration</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-600">Legal Entity Type:</span>
                        <p className="font-medium">{selectedApp.india_csr_compliance.legal_entity_type || "Not provided"}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">PAN Number:</span>
                        <p className="font-medium font-mono">{selectedApp.india_csr_compliance.pan_number || "Not provided"}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">CSR Registration:</span>
                        <p className="font-medium font-mono text-xs">{selectedApp.india_csr_compliance.csr_registration || "Not provided"}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">NGO Darpan ID:</span>
                        <p className="font-medium">{selectedApp.india_csr_compliance.ngo_darpan_id || "Not provided"}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Track Record:</span>
                        <p className="font-medium">{selectedApp.india_csr_compliance.years_track_record || "0"} years</p>
                      </div>
                    </div>
                  </div>

                  {/* Project Eligibility */}
                  <div className="space-y-3 p-3 bg-white rounded border">
                    <h4 className="font-semibold text-sm text-gray-800">Project Eligibility</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-600">CSR Activity Focus:</span>
                        <p className="font-medium">{selectedApp.india_csr_compliance.csr_activity_focus || "Not provided"}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Project Type:</span>
                        <p className="font-medium">{selectedApp.india_csr_compliance.project_type || "Not provided"}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Admin Overheads:</span>
                        <p className="font-medium">₹{selectedApp.india_csr_compliance.admin_overheads || "0"}</p>
                      </div>
                    </div>
                  </div>

                  {/* Document Verification Checklist */}
                  <div className="space-y-3 p-3 bg-white rounded border">
                    <h4 className="font-semibold text-sm text-gray-800 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Document Verification Checklist
                    </h4>
                    <div className="space-y-2">
                      {/* Incorporation Certificate */}
                      <div className="flex items-center justify-between p-2 rounded bg-gray-50">
                        <div className="flex items-center gap-2">
                          {selectedApp.india_csr_compliance.documents?.incorporation_cert ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-600" />
                          )}
                          <span className="text-sm">Incorporation Certificate</span>
                        </div>
                        {selectedApp.india_csr_compliance.documents?.incorporation_cert && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadDocument(
                              selectedApp.id,
                              'india_incorporation_cert',
                              selectedApp.india_csr_compliance.documents.incorporation_cert.filename
                            )}
                          >
                            <Download className="w-3 h-3 mr-1" />
                            Download
                          </Button>
                        )}
                      </div>

                      {/* 12A Certificate */}
                      <div className="flex items-center justify-between p-2 rounded bg-gray-50">
                        <div className="flex items-center gap-2">
                          {selectedApp.india_csr_compliance.documents?.cert_12a ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-600" />
                          )}
                          <span className="text-sm">12A Certificate</span>
                        </div>
                        {selectedApp.india_csr_compliance.documents?.cert_12a && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadDocument(
                              selectedApp.id,
                              'india_12a_cert',
                              selectedApp.india_csr_compliance.documents.cert_12a.filename
                            )}
                          >
                            <Download className="w-3 h-3 mr-1" />
                            Download
                          </Button>
                        )}
                      </div>

                      {/* 80G Certificate */}
                      <div className="flex items-center justify-between p-2 rounded bg-gray-50">
                        <div className="flex items-center gap-2">
                          {selectedApp.india_csr_compliance.documents?.cert_80g ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-600" />
                          )}
                          <span className="text-sm">80G Certificate</span>
                        </div>
                        {selectedApp.india_csr_compliance.documents?.cert_80g && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadDocument(
                              selectedApp.id,
                              'india_80g_cert',
                              selectedApp.india_csr_compliance.documents.cert_80g.filename
                            )}
                          >
                            <Download className="w-3 h-3 mr-1" />
                            Download
                          </Button>
                        )}
                      </div>

                      {/* Financial Statements */}
                      <div className="flex items-center justify-between p-2 rounded bg-gray-50">
                        <div className="flex items-center gap-2">
                          {selectedApp.india_csr_compliance.documents?.financial_statements ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-600" />
                          )}
                          <span className="text-sm">3 Years Financial Statements</span>
                        </div>
                        {selectedApp.india_csr_compliance.documents?.financial_statements && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadDocument(
                              selectedApp.id,
                              'india_financial_statements',
                              selectedApp.india_csr_compliance.documents.financial_statements.filename
                            )}
                          >
                            <Download className="w-3 h-3 mr-1" />
                            Download
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Compliance Status Summary */}
                  <div className={`p-3 rounded border-2 ${
                    selectedApp.india_csr_compliance.legal_entity_type &&
                    selectedApp.india_csr_compliance.pan_number &&
                    selectedApp.india_csr_compliance.csr_registration &&
                    selectedApp.india_csr_compliance.documents?.incorporation_cert &&
                    selectedApp.india_csr_compliance.documents?.cert_12a &&
                    selectedApp.india_csr_compliance.documents?.cert_80g &&
                    selectedApp.india_csr_compliance.documents?.financial_statements
                      ? 'bg-green-50 border-green-300'
                      : 'bg-yellow-50 border-yellow-300'
                  }`}>
                    <div className="flex items-center gap-2">
                      {selectedApp.india_csr_compliance.legal_entity_type &&
                       selectedApp.india_csr_compliance.pan_number &&
                       selectedApp.india_csr_compliance.csr_registration &&
                       selectedApp.india_csr_compliance.documents?.incorporation_cert &&
                       selectedApp.india_csr_compliance.documents?.cert_12a &&
                       selectedApp.india_csr_compliance.documents?.cert_80g &&
                       selectedApp.india_csr_compliance.documents?.financial_statements ? (
                        <>
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <span className="font-semibold text-green-800">CSR Compliance Complete</span>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="w-5 h-5 text-yellow-600" />
                          <span className="font-semibold text-yellow-800">CSR Compliance Incomplete</span>
                        </>
                      )}
                    </div>
                    <p className="text-xs mt-1 text-gray-700">
                      Review all documents and verify information before approval
                    </p>
                  </div>
                </div>
              )}

              {/* Admin Notes */}
              <div>
                <Label htmlFor="admin_notes">Admin Notes</Label>
                <textarea
                  id="admin_notes"
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  className="w-full min-h-[100px] p-3 border rounded-lg text-sm"
                  placeholder="Add notes about this application..."
                />
              </div>

              {/* Actions */}
              {selectedApp.status !== 'approved' && selectedApp.status !== 'rejected' && (
                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    onClick={() => approveApplication(selectedApp.id)}
                    disabled={processing}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {processing ? "Processing..." : (applicationType === "club" ? "Approve & Create Club" : "Approve Application")}
                  </Button>
                  <Button
                    onClick={() => updateStatus(selectedApp.id, 'under_review')}
                    disabled={processing}
                    variant="outline"
                    className="flex-1"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Mark Under Review
                  </Button>
                  <Button
                    onClick={() => updateStatus(selectedApp.id, 'rejected')}
                    disabled={processing}
                    variant="destructive"
                    className="flex-1"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                </div>
              )}

              {selectedApp.status === 'approved' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 text-green-800">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Application Approved</span>
                  </div>
                  <p className="text-sm text-green-700 mt-2">
                    Club account created. Welcome email sent to {selectedApp.contact.email}
                  </p>
                </div>
              )}

              {selectedApp.status === 'rejected' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2 text-red-800">
                    <XCircle className="w-5 h-5" />
                    <span className="font-medium">Application Rejected</span>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default ApplicationsTab;
